import { AxiosError } from 'axios';
import { ApiHostKey, resolveApiHost } from './apiHosts';

/**
 * How much a single HTTP failure tells us about the session.
 *
 * - `certain` — this failure only happens when the token is rejected. Act on it.
 * - `probe`   — ambiguous. Ask Core before doing anything drastic.
 * - `ignore`  — tells us nothing about the session. Never act, never probe.
 *
 * No backend the Provider App talks to has a status that is reserved for a dead
 * token (Core and the node services reuse 401/403 for authorization), so no rule
 * yields `certain` today; it is kept for an explicit contract such as
 * appointments-api's `SESSION_INVALID`.
 */
export type Suspicion = 'ignore' | 'probe' | 'certain';

/** Coarse, non-identifying facts only: never a URL, a body or a token. */
export interface FailureContext {
  suspicion: Suspicion;
  host: ApiHostKey | null;
  status: number | null;
  coreVersion: string | null;
  code: string | null;
}

const NODE_HOSTS: ApiHostKey[] = [
  'nodeLegacy',
  'members',
  'healthRecords',
  'schedule',
  'dashboards',
  'onboarding',
  'reviewList',
  'appointments',
];

/**
 * Sign-in, password reset/change and impersonation. A 401 here means "wrong
 * credentials" or "not allowed", not "your session expired".
 */
const isCredentialRequest = (path: string) =>
  /^\/v1\/(auth|profile\/change_password|users\/login_as)(?:[/?#]|$)/.test(path);

const coreVersionOf = (path: string) => /^\/(v\d+)(?:[/?#]|$)/.exec(path)?.[1] ?? null;

/** The node services' request-helper answer when no user owns the token. */
const isUnknownTokenBody = (body: unknown) => {
  const reason = (body as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' && /user \(with token\) not found/.test(reason);
};

const safeCode = (body: unknown) => {
  const code = (body as { code?: unknown } | null)?.code;
  return typeof code === 'string' && /^[A-Z_]{1,40}$/.test(code) ? code : null;
};

export const classifyFailure = (error: AxiosError): FailureContext => {
  const { host, path } = resolveApiHost(error?.config);
  const status = error?.response?.status ?? null;
  const body: unknown = error?.response?.data;
  const coreVersion = host === 'core' ? coreVersionOf(path) : null;

  const context: FailureContext = {
    suspicion: 'ignore',
    host,
    status,
    coreVersion,
    code: safeCode(body),
  };
  const probe = (): FailureContext => ({ ...context, suspicion: 'probe' });

  // No response at all: network down, CORS, timeout, or a cancelled request.
  // Core being unreachable is not a dead session, and probing in this state is
  // how you get a probe storm during an outage.
  if (!error?.response) {
    return context;
  }

  // Unrecognised host (features, third parties on the default instance).
  if (!host) {
    return context;
  }

  if (host === 'core') {
    if (isCredentialRequest(path)) {
      return context;
    }
    // A dead token is a 401 on v1/v4 and a 403 on v2/v3, but both statuses are
    // also ordinary authorization failures, so only the probe can decide.
    if (status === 401 && coreVersion && /^v[1-4]$/.test(coreVersion)) {
      return probe();
    }
    if (status === 403 && (coreVersion === 'v2' || coreVersion === 'v3')) {
      return probe();
    }
    // v1 403/404 are permission and not-found answers.
    return context;
  }

  if (NODE_HOSTS.includes(host)) {
    if (status === 401) {
      return probe();
    }
    // Tested in place: that body carries the raw token, so it is never copied.
    if (status === 404 && isUnknownTokenBody(body)) {
      return probe();
    }
    return context;
  }

  // The content service's 401 is never conclusive on its own: Core decides.
  if ((host === 'billing' || host === 'content') && status === 401) {
    return probe();
  }

  // Everything else (node 403, 429, 5xx…) says nothing about the session, and
  // probing on 5xx would hammer a struggling backend.
  return context;
};

/** Whether a failure looks like it could be a rejected session. */
export const isAuthShapedFailure = (error: unknown) =>
  classifyFailure(error as AxiosError).suspicion !== 'ignore';
